import { fetch } from "../lib/network";
import AppSettings from "../config";
import queryString from "query-string";
import networkSelectors from "../selectors/network";
import { createAction } from "redux-api-middleware";
import { Dispatch } from "redux";
import { FileUsage } from "./coreUpload";
import { coreService } from "../services/coreService";

export const UPLOAD_LOADING = "NEW_UPLOAD_LOADING";
export const UPLOAD_LOADED = "NEW_UPLOAD_LOADED";
export const UPLOAD_FAILED = "NEW_UPLOAD_FAILED";
export const MINIMUM_CHUNK_SIZE = 5 * 1024 * 1024; // 5MiB - This is the absolute minimum size for S3 multipart uploads
export const MAXIMUM_PARTS = 10000; // Maximum number of parts for S3 multipart uploads

function loadingMultipartUpload() {
  return {
    type: UPLOAD_LOADING,
  };
}

function loadedMultipartUpload(id: string, json: any) {
  return {
    type: UPLOAD_LOADED,
    uploadId: id,
    payload: json,
  };
}

function failedMultipartUpload() {
  return {
    type: UPLOAD_FAILED,
  };
}

type MultipartUploadResponse = Awaited<ReturnType<typeof coreService.fileMultipartCreate>>["data"];
async function newMultipartUpload(dispatch: Dispatch, file: File, usage: FileUsage): Promise<MultipartUploadResponse> {
  const adjustedChunkSize = Math.max(MINIMUM_CHUNK_SIZE, Math.ceil(file.size / MAXIMUM_PARTS));
  const query = queryString.stringify({
    usage,
    fileName: file.name,
    mimeType: file.type,
    numParts: Math.ceil(file.size / adjustedChunkSize),
  });

  const resp = await dispatch(
    createAction({
      endpoint: `${AppSettings.FILE_UPLOAD_API_URL}/multipart?${query}`,
      method: "POST",
      headers: networkSelectors.defaultHeaders,
      types: [UPLOAD_LOADING, UPLOAD_LOADED, UPLOAD_FAILED],
      body: JSON.stringify({}),
    }),
  );

  return resp.payload as MultipartUploadResponse;
}

async function performMultipartUpload(json: MultipartUploadResponse, file: File) {
  const options = {
    method: "PUT",
    body: file,
    headers: {
      "Content-Type": file.type,
    },
  };
  const adjustedChunkSize = Math.max(MINIMUM_CHUNK_SIZE, Math.ceil(file.size / MAXIMUM_PARTS));
  const parts = await Promise.all(
    json.urls.map(async (url, index) => {
      const start = index * adjustedChunkSize;
      const end = Math.min(start + adjustedChunkSize, file.size);
      const chunk = file.slice(start, end);
      const response = await fetch(url, {
        ...options,
        body: chunk,
      });
      if (response.status >= 400) throw new Error(`Failed to upload chunk ${index + 1}`);
      return {
        eTag: response.headers.get("ETag") ?? "",
        partNumber: index + 1,
      };
    }),
  );
  return parts;
}

type UploadPart = { eTag: string; partNumber: number };
type MultiPartFinalizationResponse = Awaited<ReturnType<typeof coreService.fileMultipartFinalizedCreate>>["data"];
async function finalizeMultipartUpload(dispatch: Dispatch, uuid: string, uploadId: string, parts: UploadPart[]) {
  const resp = await dispatch(
    createAction({
      endpoint: `${AppSettings.FILE_UPLOAD_API_URL}/multipart/${uuid}/finalized`,
      method: "POST",
      headers: networkSelectors.defaultHeaders,
      types: [UPLOAD_LOADING, UPLOAD_LOADED, UPLOAD_FAILED],
      body: JSON.stringify({
        uploadId,
        parts,
      }),
    }),
  );
  return {
    ...(resp.payload as MultiPartFinalizationResponse),
    uuid,
  };
}

export function multipartUpload({ id, file, usage }: { id: string; file: File; usage?: FileUsage }) {
  if (!usage) throw new Error("usage is required when using multipartUpload");

  return (dispatch: Dispatch) => {
    dispatch(loadingMultipartUpload());
    return newMultipartUpload(dispatch, file, usage as FileUsage)
      .then((json) =>
        performMultipartUpload(json, file).then((parts) =>
          finalizeMultipartUpload(dispatch, json.uuid, json.uploadId, parts).then((finalResponse) =>
            dispatch(loadedMultipartUpload(id, finalResponse)),
          ),
        ),
      )
      .catch(() => dispatch(failedMultipartUpload()));
  };
}
